import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck, Calendar, DollarSign, User, TrendingUp, Filter, Download, MapPin, Flame, Bike
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CarregandoBloco, CarregandoCards, CarregandoLista } from "../components/carregando";

const DeliveryHeatMap = lazy(() => import("../components/DeliveryHeatMap"));

const API = '/api';

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// Formatar data como YYYY-MM-DD no fuso local (evita conversão UTC do toISOString)
const toLocalDateStr = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// R$ 1.347,87 (antes "R$ 1347,87", sem o ponto do milhar) e 23,4% (antes "23.4%").
const fmtReais = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtPct = (v) => `${(Number(v) || 0).toFixed(1).replace(".", ",")}%`;
// "(34) 99123-4501" / "(34) 3212-4501". Antes saía "(34)9.9123-4501" (achado 353).
const fmtTelefone = (tel) => {
  const d = String(tel || "").replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return tel;
};
// Teto do /pedidos/search. Período maior que isso mostra o aviso embaixo dos cards.
const LIMITE_PEDIDOS = 5000;

export default function RelatorioEntregas() {
  const [activeTab, setActiveTab] = useState("entregadores");
  const [entregadores, setEntregadores] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  // Carregamento com cards (25/10/2026): `primeiraCarga` só vale até a 1ª
  // resposta (esqueleto); trocar a data depois só esmaece (`buscandoPedidos`).
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [buscandoPedidos, setBuscandoPedidos] = useState(false);
  const ultimaBusca = useRef(0);

  // Filtros de data
  const [dataInicio, setDataInicio] = useState(() => {
    const date = new Date();
    date.setDate(1); // Primeiro dia do mês
    return toLocalDateStr(date);
  });
  const [dataFim, setDataFim] = useState(() => {
    return toLocalDateStr(new Date());
  });
  const [bairros, setBairros] = useState([]);

  // Entregadores e bairros: uma vez só, ao abrir.
  const [cadastrosProntos, setCadastrosProntos] = useState(false);
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/entregadores`, getAuthHeader()),
      axios.get(`${API}/bairros`, getAuthHeader()),
    ])
      .then(([entregadoresRes, bairrosRes]) => {
        setEntregadores(entregadoresRes.data.filter(e => e.ativo));
        setBairros(bairrosRes.data);
      })
      .catch((error) => {
        console.error("Erro ao carregar entregadores:", error);
        toast.error("Erro ao carregar entregadores");
      })
      .finally(() => setCadastrosProntos(true));
  }, []);

  // Pedidos DO PERÍODO escolhido (25/10/2026). Antes vinha `GET /pedidos` sem
  // filtro — só os 500 pedidos mais recentes da loja — e o "Este mês" de loja
  // movimentada saía incompleto (achado 82); mês passado chegava a sair zerado.
  const fetchPedidos = useCallback(async () => {
    if (!dataInicio || !dataFim || dataInicio > dataFim) return;
    const minha = ++ultimaBusca.current;
    setBuscandoPedidos(true);
    try {
      const res = await axios.get(`${API}/pedidos/search`, {
        ...getAuthHeader(),
        params: {
          date_from: `${dataInicio}T00:00:00-03:00`,
          date_to: `${dataFim}T23:59:59-03:00`,
          limit: LIMITE_PEDIDOS,
        },
      });
      if (minha === ultimaBusca.current) setPedidos(res.data || []);
    } catch (error) {
      if (minha !== ultimaBusca.current) return;
      console.error("Erro ao carregar pedidos:", error);
      toast.error("Erro ao carregar os pedidos do período");
    } finally {
      if (minha === ultimaBusca.current) {
        setBuscandoPedidos(false);
        setPrimeiraCarga(false);
      }
    }
  }, [dataInicio, dataFim]);

  useEffect(() => {
    fetchPedidos();
  }, [fetchPedidos]);

  const carregandoTela = primeiraCarga || !cadastrosProntos;

  // Filtrar pedidos por período e status concluído
  const pedidosFiltrados = pedidos.filter(p => {
    if (p.status !== 'concluido') return false;
    if (p.tipo_entrega !== 'delivery') return false;
    if (!p.entregador_id) return false;

    const dataPedido = toLocalDateStr(p.created_at);
    return dataPedido >= dataInicio && dataPedido <= dataFim;
  });

  // Calcular estatísticas por entregador
  const estatisticasEntregadores = entregadores.map(entregador => {
    const entregas = pedidosFiltrados.filter(p => p.entregador_id === entregador.id);
    const totalEntregas = entregas.length;

    // Calcular valor total das entregas (soma dos valores de entrega dos pedidos ou bairros)
    const valorTotal = entregas.reduce((acc, p) => {
      // Primeiro tentar usar o valor de entrega salvo no pedido
      let valorEntrega = p.valor_entrega;
      if (valorEntrega === undefined || valorEntrega === null) {
        // Buscar valor do bairro se não tiver no pedido
        const bairro = bairros.find(b => b.nome === p.endereco_bairro);
        valorEntrega = bairro?.valor_entrega || 0;
      }
      return acc + (valorEntrega || 0);
    }, 0);

    return {
      ...entregador,
      totalEntregas,
      valorTotal
    };
  }).sort((a, b) => b.totalEntregas - a.totalEntregas);

  // Totais gerais
  const totalGeral = {
    entregas: estatisticasEntregadores.reduce((acc, e) => acc + e.totalEntregas, 0),
    valor: estatisticasEntregadores.reduce((acc, e) => acc + e.valorTotal, 0)
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-6 pb-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Truck className="w-7 h-7 text-primary" />
              Relatório de Entregas
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Acompanhe o desempenho dos entregadores
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4">
        <div className="flex gap-1 bg-muted/50 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab("entregadores")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === "entregadores"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Entregadores
          </button>
          <button
            onClick={() => setActiveTab("mapa")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === "mapa"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Flame className="w-4 h-4" />
            Mapa de Calor
          </button>
        </div>
      </div>

      {/* Conteúdo das abas */}
      {activeTab === "entregadores" ? (
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Filtros de Data */}
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">Filtrar por Período</h3>
            </div>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[150px]">
                <Label className="text-xs text-muted-foreground">Data Início</Label>
                <Input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <Label className="text-xs text-muted-foreground">Data Fim</Label>
                <Input
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button variant="outline" onClick={() => {
                  const hoje = new Date();
                  setDataInicio(toLocalDateStr(hoje));
                  setDataFim(toLocalDateStr(hoje));
                }}>
                  Hoje
                </Button>
                <Button variant="outline" onClick={() => {
                  const hoje = new Date();
                  const inicioSemana = new Date(hoje);
                  inicioSemana.setDate(hoje.getDate() - hoje.getDay());
                  setDataInicio(toLocalDateStr(inicioSemana));
                  setDataFim(toLocalDateStr(hoje));
                }}>
                  Esta Semana
                </Button>
                <Button variant="outline" onClick={() => {
                  const hoje = new Date();
                  const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
                  setDataInicio(toLocalDateStr(inicioMes));
                  setDataFim(toLocalDateStr(hoje));
                }}>
                  Este Mês
                </Button>
              </div>
            </div>
          </div>

          {carregandoTela ? (
            <>
              <CarregandoCards quantos={3} className="grid grid-cols-1 md:grid-cols-3 gap-4" altura="h-[100px]" />
              <CarregandoLista linhas={5} colunas={2} />
            </>
          ) : (
          <div className={`space-y-6 transition-opacity duration-200 ${buscandoPedidos ? "opacity-50 pointer-events-none" : ""}`}>
          {/* Cards de Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-xl border border-blue-500/20 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total de Entregas</p>
                  <p className="text-3xl font-bold text-blue-600">{totalGeral.entregas}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Truck className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-xl border border-green-500/20 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Valor Total Pago</p>
                  <p className="text-3xl font-bold text-green-600">{fmtReais(totalGeral.valor)}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-xl border border-purple-500/20 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Entregadores Ativos</p>
                  <p className="text-3xl font-bold text-purple-600">{entregadores.length}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {pedidos.length >= LIMITE_PEDIDOS && (
            <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              Período muito longo: a conta usa os {LIMITE_PEDIDOS.toLocaleString("pt-BR")} pedidos mais recentes dele. Escolha um período menor para ver tudo.
            </p>
          )}

          {/* Lista de Entregadores */}
          <div className="bg-card rounded-xl border overflow-hidden">
            <div className="p-4 border-b bg-muted/30">
              <h3 className="font-semibold flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Desempenho por Entregador
              </h3>
            </div>

            {estatisticasEntregadores.length === 0 ? (
              <div className="p-8 text-center">
                <Truck className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">Nenhum entregador cadastrado</p>
              </div>
            ) : (
              <div className="divide-y">
                {estatisticasEntregadores.map((entregador, index) => (
                  <div key={entregador.id} className="p-4 hover:bg-muted/30 transition-colors">
                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2">
                      {/* Posição */}
                      <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-yellow-500/20 text-yellow-600' :
                        index === 1 ? 'bg-gray-300/30 text-gray-600 dark:text-gray-300' :
                        index === 2 ? 'bg-orange-500/20 text-orange-600' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {index + 1}º
                      </div>

                      {/* Avatar e Nome */}
                      {(() => {
                        const isEntregaAi = entregador.nome?.startsWith('[UP Entrega]') || entregador.nome?.startsWith('[Entrega Aí]');
                        const displayName = isEntregaAi ? entregador.nome.replace('[UP Entrega] ', '').replace('[Entrega Aí] ', '') : entregador.nome;
                        const photoUrl = entregador.foto_url && (entregador.foto_url.startsWith('http') ? entregador.foto_url : `${API}${entregador.foto_url.startsWith('/') ? '' : '/'}${entregador.foto_url}`);
                        return (
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="relative flex-shrink-0">
                              {photoUrl ? (
                                <img src={photoUrl} alt={displayName} className={`w-10 h-10 rounded-full object-cover border-2 ${isEntregaAi ? 'border-[#0ea5e9]' : 'border-primary/30'}`} />
                              ) : (
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isEntregaAi ? 'bg-[#0ea5e9]/10 border-2 border-[#0ea5e9]' : 'bg-primary/10'}`}>
                                  {isEntregaAi ? <img src="/logo-upentrega-v3.png" alt="UP" className="w-6 h-6 object-contain" /> : <User className="w-5 h-5 text-primary" />}
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold flex items-center gap-1.5 min-w-0">
                                <span className="truncate">{displayName}</span>
                                {isEntregaAi && (
                                  <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#0ea5e9]/10 text-[#0ea5e9]">
                                    <img src="/logo-upentrega-v3.png" alt="" className="w-3 h-3 object-contain" /> UP Entrega
                                  </span>
                                )}
                              </p>
                              {entregador.telefone ? (
                                <a
                                  href={`https://wa.me/55${entregador.telefone.replace(/\D/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block truncate text-xs text-green-600 hover:text-green-500 hover:underline"
                                >
                                  {fmtTelefone(entregador.telefone)}
                                </a>
                              ) : (
                                <p className="text-xs text-muted-foreground">Sem telefone</p>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Estatísticas */}
                      <div className="flex items-center justify-around sm:justify-start gap-6 w-full sm:w-auto">
                        <div className="text-center">
                          <p className="text-xl sm:text-2xl font-bold text-blue-600">{entregador.totalEntregas}</p>
                          <p className="text-xs text-muted-foreground">entregas</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl sm:text-2xl font-bold text-green-600">{fmtReais(entregador.valorTotal)}</p>
                          <p className="text-xs text-muted-foreground">ganhos</p>
                        </div>
                      </div>
                    </div>

                    {/* Barra de progresso visual */}
                    {totalGeral.entregas > 0 && (
                      <div className="mt-3 ml-12">
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-primary to-primary/60 rounded-full transition-all"
                            style={{ width: `${(entregador.totalEntregas / totalGeral.entregas) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {fmtPct((entregador.totalEntregas / totalGeral.entregas) * 100)} do total
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          </div>
          )}

          {/* Info sobre valor de entrega */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Nota:</strong> O valor de cada entrega é calculado com base no valor de entrega do bairro configurado em Localização → Bairros.
              Entregas sem bairro cadastrado usam o valor padrão de R$ 5,00.
            </p>
          </div>
        </div>
      ) : (
        /* Aba Mapa de Calor */
        <div className="flex-1 p-6 pt-4 overflow-hidden">
          <div className="h-full rounded-xl border bg-card overflow-hidden">
            {/* Enquanto o mapa (carregado à parte) chega: bloco piscando, não círculo. */}
            <Suspense fallback={<CarregandoBloco altura="h-full" className="border-0 rounded-none" />}>
              <DeliveryHeatMap />
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
}
